import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, apiFetch } from "@/lib/api";
import { toLocalISODate } from "@/lib/dateUtils";

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = toLocalISODate;

const presets = () => {
  const now = new Date();
  const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return {
    week: [iso(startOfWeek), iso(now)],
    month: [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)],
    year: [iso(new Date(now.getFullYear(), 0, 1)), iso(now)],
  };
};

const Section = ({ title, total, rows, cols }) => (
  <div className="border rounded-md overflow-hidden">
    <div className="flex justify-between items-center bg-gray-50 px-3 py-2 border-b">
      <span className="font-semibold text-sm">{title}</span>
      <span className="font-bold text-sm">{money(total)}</span>
    </div>
    {rows.length === 0 ? (
      <p className="text-xs text-gray-400 px-3 py-2">No entries</p>
    ) : rows.map((r, i) => (
      <div key={i} className="grid grid-cols-12 px-3 py-1.5 text-xs border-t">
        <span className="col-span-2 text-gray-500">{r.date}</span>
        <span className="col-span-6">{cols(r)}</span>
        <span className="col-span-4 text-right">{money(r.amount)}</span>
      </div>
    ))}
  </div>
);

// Accrual P&L line item row: { category, current_amount }
const PLLine = ({ label, amount, bold, muted, testId }) => (
  <div className={`flex justify-between px-3 py-1.5 text-sm ${bold ? "font-bold border-t" : ""}`} data-testid={testId}>
    <span className={muted ? "text-gray-500" : ""}>{label}</span>
    <span className={muted ? "text-gray-500" : ""}>{money(amount)}</span>
  </div>
);

const ProfitLossReport = ({ open, onOpenChange }) => {
  const p = presets();
  const [range, setRange] = useState(p.month);
  const [active, setActive] = useState("month");
  const [mode, setMode] = useState("cash"); // "cash" (Cash Flow Statement) | "accrual" (Profit & Loss Report)
  const [data, setData] = useState(null);
  const [accrualData, setAccrualData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (r) => {
    setLoading(true);
    try {
      const [cashRes, accrualRes] = await Promise.all([
        apiFetch(`${API}/reports/profit-loss?start=${r[0]}&end=${r[1]}`),
        apiFetch(`${API}/reports/profit-loss-accrual?start=${r[0]}&end=${r[1]}`),
      ]);
      setData(await cashRes.json());
      setAccrualData(await accrualRes.json());
    } catch (e) { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(range); }, [open]);

  const applyPreset = (key) => { const pr = presets(); setActive(key); setRange(pr[key]); load(pr[key]); };
  const applyCustom = () => { setActive("custom"); load(range); };

  const printCashFlow = () => {
    const sec = (title, obj, label) => `<tr><td colspan="2" style="padding:8px;font-weight:bold;background:#eef">${title}</td><td style="padding:8px;text-align:right;font-weight:bold;background:#eef">${money(obj.total)}</td></tr>` +
      (obj.rows || obj.invoices || []).map(r => `<tr><td style="border:1px solid #ddd;padding:5px 8px">${r.date}</td><td style="border:1px solid #ddd;padding:5px 8px">${r[label]||r.description||''}${r.vehicle?(' • '+r.vehicle):''}</td><td style="border:1px solid #ddd;padding:5px 8px;text-align:right">${money(r.amount)}</td></tr>`).join("");
    const html = `<!doctype html><html><head><title>Cash Flow Statement</title><style>body{font-family:Arial;padding:24px}table{border-collapse:collapse;width:100%;font-size:13px}h1{font-size:18px;margin:0}.muted{color:#666;font-size:12px}</style></head><body>
      <h1>GARAGE — Cash Flow Statement</h1><div class="muted">${data.start} to ${data.end} • Cash actually received minus cash actually paid out</div>
      <table style="margin-top:14px">
        <tr><td colspan="2" style="padding:8px;font-weight:bold;background:#dfe">CASH IN (Payments Received)</td><td style="padding:8px;text-align:right;font-weight:bold;background:#dfe">${money(data.revenue.total)}</td></tr>
        ${(data.revenue.invoices||[]).map(r=>`<tr><td style="border:1px solid #ddd;padding:5px 8px">${r.date}</td><td style="border:1px solid #ddd;padding:5px 8px">${r.kind==='Advance'?'[Advance] ':''}${r.invoice_number||''} • ${r.customer_name||''} • ${r.vehicle||''}</td><td style="border:1px solid #ddd;padding:5px 8px;text-align:right">${money(r.amount)}</td></tr>`).join("")}
        ${sec("SALARIES PAID", data.expenses.salaries, "party")}
        ${sec("SUPPLIER / PO PAYMENTS PAID", data.expenses.purchase_orders, "party")}
        ${sec("GARAGE EXPENSES PAID", data.expenses.garage, "description")}
        <tr><td colspan="2" style="padding:8px;font-weight:bold;background:#fee">TOTAL CASH OUT</td><td style="padding:8px;text-align:right;font-weight:bold;background:#fee">${money(data.expenses.total)}</td></tr>
        <tr><td colspan="2" style="padding:10px;font-weight:bold;font-size:15px">NET CASH ${data.net_profit>=0?'GAIN':'SHORTFALL'}</td><td style="padding:10px;text-align:right;font-weight:bold;font-size:15px;color:${data.net_profit>=0?'green':'red'}">${money(Math.abs(data.net_profit))}</td></tr>
      </table></body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const printAccrual = () => {
    const d = accrualData;
    const row = (label, amt, hl) => `<tr><td style="padding:6px 8px;${hl?'font-weight:bold;background:#eef':''}">${label}</td><td style="padding:6px 8px;text-align:right;${hl?'font-weight:bold;background:#eef':''}">${money(amt)}</td></tr>`;
    const html = `<!doctype html><html><head><title>Profit & Loss Report</title><style>body{font-family:Arial;padding:24px}table{border-collapse:collapse;width:100%;font-size:13px}h1{font-size:18px;margin:0}.muted{color:#666;font-size:12px}</style></head><body>
      <h1>GARAGE — Profit &amp; Loss Report</h1><div class="muted">${d.period_start} to ${d.period_end} • Billed revenue minus true cost of goods &amp; operating overhead</div>
      <table style="margin-top:14px">
        ${row("Service Revenue", d.service_revenue.current_amount)}
        ${row("Parts Revenue", d.parts_revenue.current_amount)}
        ${row("Other Revenue", d.other_revenue.current_amount)}
        ${row("TOTAL REVENUE", d.total_revenue.current_amount, true)}
        ${row("Parts Consumed (COGS)", -d.parts_expense.current_amount)}
        ${row("GROSS PROFIT", d.gross_profit.current_amount, true)}
        ${row("Employee Expense", -d.employee_expense.current_amount)}
        ${row("Operational Expense", -d.operational_expense.current_amount)}
        ${row("Utilities", -d.utilities_expense.current_amount)}
        ${row("Rent", -d.rent_expense.current_amount)}
        ${row("Maintenance", -d.maintenance_expense.current_amount)}
        ${row("Equipment", -d.equipment_expense.current_amount)}
        ${row("Supplies", -d.supplies_expense.current_amount)}
        ${row("Other Expense", -d.other_expense.current_amount)}
        <tr><td colspan="2" style="padding:10px;font-weight:bold;font-size:15px;border-top:2px solid #333">NET ${d.net_profit.current_amount>=0?'PROFIT':'LOSS'}</td></tr>
        <tr><td style="padding:0 8px 10px"></td><td style="padding:0 8px 10px;text-align:right;font-weight:bold;font-size:15px;color:${d.net_profit.current_amount>=0?'green':'red'}">${money(Math.abs(d.net_profit.current_amount))}</td></tr>
      </table>
      <p class="muted" style="margin-top:10px">Note: supplier/PO payments and customer advances are intentionally excluded - they settle a payable or sit as a liability, not P&amp;L revenue/expense. See the separate Cash Flow Statement for actual money in/out of the bank.</p>
      </body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="pl-report-dialog">
        <DialogHeader>
          <DialogTitle>Financial Reports</DialogTitle>
          <DialogDescription>
            {mode === "cash"
              ? "Cash Flow Statement: actual cash received minus actual cash paid out (salaries, supplier/PO payments, garage expenses). Tracks your wallet."
              : "Profit & Loss Report: billed revenue minus true parts cost (COGS) and real operating overhead. Supplier payments and advances are excluded. Tracks your business's actual profitability."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 border-b pb-2">
          <Button size="sm" variant={mode === "cash" ? "default" : "outline"} onClick={() => setMode("cash")} data-testid="pl-mode-cash">Cash Flow Statement</Button>
          <Button size="sm" variant={mode === "accrual" ? "default" : "outline"} onClick={() => setMode("accrual")} data-testid="pl-mode-accrual">Profit &amp; Loss Report</Button>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          {[["week", "This Week"], ["month", "This Month"], ["year", "This Year"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={active === k ? "default" : "outline"} onClick={() => applyPreset(k)} data-testid={`pl-preset-${k}`}>{l}</Button>
          ))}
          <div className="flex items-end gap-1 ml-2">
            <div><Label className="text-xs">From</Label><Input type="date" className="h-8 w-36" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} data-testid="pl-start" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" className="h-8 w-36" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} data-testid="pl-end" /></div>
            <Button size="sm" variant="outline" onClick={applyCustom} data-testid="pl-apply">Apply</Button>
          </div>
          <Button size="sm" onClick={mode === "cash" ? printCashFlow : printAccrual} disabled={!data || !accrualData} data-testid="pl-print">Print / PDF</Button>
        </div>

        {loading || !data || !accrualData ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
        ) : mode === "cash" ? (
          <div className="space-y-3" data-testid="pl-cash-view">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Cash In</div><div className="text-lg font-bold text-emerald-700" data-testid="pl-revenue">{money(data.revenue.total)}</div></div>
              <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Cash Out</div><div className="text-lg font-bold text-red-600" data-testid="pl-expenses">{money(data.expenses.total)}</div></div>
              <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Net Cash {data.net_profit >= 0 ? "Gain" : "Shortfall"}</div><div className={`text-lg font-bold ${data.net_profit >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="pl-net">{money(Math.abs(data.net_profit))}</div></div>
            </div>

            <Section title={`Cash In — Payments Received (${data.revenue.count})`} total={data.revenue.total} rows={data.revenue.invoices} cols={(r) => `${r.kind === 'Advance' ? '[Advance] ' : ''}${r.invoice_number || ""} • ${r.customer_name || ""} • ${r.vehicle || ""}`} />
            <Section title="Salaries Paid" total={data.expenses.salaries.total} rows={data.expenses.salaries.rows} cols={(r) => `${r.description || ""}${r.party ? " • " + r.party : ""}`} />
            <Section title="Supplier / PO Payments Paid" total={data.expenses.purchase_orders.total} rows={data.expenses.purchase_orders.rows} cols={(r) => `${r.description || ""}${r.party ? " • " + r.party : ""}`} />
            <Section title="Garage Expenses Paid" total={data.expenses.garage.total} rows={data.expenses.garage.rows} cols={(r) => r.description || ""} />
            {data.pending_advances?.total > 0 && (
              <div className="rounded-md border border-dashed p-3" data-testid="pl-pending-advances">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-amber-700">Advance Payments (Not Yet Invoiced)</span>
                  <span className="font-medium text-amber-600">{money(data.pending_advances.total)}</span>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">{data.pending_advances.note}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="pl-accrual-view">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Total Revenue</div><div className="text-lg font-bold text-emerald-700" data-testid="pl-accrual-revenue">{money(accrualData.total_revenue.current_amount)}</div></div>
              <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Gross Profit</div><div className="text-lg font-bold text-blue-700" data-testid="pl-accrual-gross-profit">{money(accrualData.gross_profit.current_amount)}</div></div>
              <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Net {accrualData.net_profit.current_amount >= 0 ? "Profit" : "Loss"}</div><div className={`text-lg font-bold ${accrualData.net_profit.current_amount >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="pl-accrual-net">{money(Math.abs(accrualData.net_profit.current_amount))}</div></div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b font-semibold text-sm">Revenue (Billed)</div>
              <PLLine label="Service Revenue" amount={accrualData.service_revenue.current_amount} testId="pl-line-service-revenue" />
              <PLLine label="Parts Revenue" amount={accrualData.parts_revenue.current_amount} testId="pl-line-parts-revenue" />
              <PLLine label="Other Revenue" amount={accrualData.other_revenue.current_amount} testId="pl-line-other-revenue" />
              <PLLine label="Total Revenue" amount={accrualData.total_revenue.current_amount} bold testId="pl-line-total-revenue" />
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b font-semibold text-sm">Cost of Goods Sold</div>
              <PLLine label="Parts Consumed (COGS)" amount={-accrualData.parts_expense.current_amount} testId="pl-line-cogs" />
              <PLLine label="Gross Profit" amount={accrualData.gross_profit.current_amount} bold testId="pl-line-gross-profit" />
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b font-semibold text-sm">Operating Overhead (excludes supplier/PO payments)</div>
              <PLLine label="Employee" amount={-accrualData.employee_expense.current_amount} testId="pl-line-employee" />
              <PLLine label="Operational" amount={-accrualData.operational_expense.current_amount} testId="pl-line-operational" />
              <PLLine label="Utilities" amount={-accrualData.utilities_expense.current_amount} testId="pl-line-utilities" />
              <PLLine label="Rent" amount={-accrualData.rent_expense.current_amount} testId="pl-line-rent" />
              <PLLine label="Maintenance" amount={-accrualData.maintenance_expense.current_amount} testId="pl-line-maintenance" />
              <PLLine label="Equipment" amount={-accrualData.equipment_expense.current_amount} testId="pl-line-equipment" />
              <PLLine label="Supplies" amount={-accrualData.supplies_expense.current_amount} testId="pl-line-supplies" />
              <PLLine label="Other" amount={-accrualData.other_expense.current_amount} testId="pl-line-other-expense" />
            </div>

            <div className="rounded-md border p-3 flex justify-between items-center bg-gray-50">
              <span className="font-bold">Net {accrualData.net_profit.current_amount >= 0 ? "Profit" : "Loss"}</span>
              <span className={`font-bold text-lg ${accrualData.net_profit.current_amount >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="pl-net-profit-final">{money(Math.abs(accrualData.net_profit.current_amount))}</span>
            </div>
            <p className="text-xs text-gray-500">Supplier/PO payments and customer advances are intentionally excluded from this report - they settle a payable or sit as a liability, not P&amp;L revenue/expense. Switch to "Cash Flow Statement" to see actual money in/out of the bank.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfitLossReport;
